// Shared building blocks for the report surfaces (Challenge Result + Progress
// Report): the category bars with the over/under strip, the front office
// ledger card, and their styles. Both pages render <ReportBitsStyles /> once.

import { helpers } from "../util/helpers.ts";
import type { View } from "../../common/types.ts";

type Card = NonNullable<View<"challengeResult">["card"]>;
type Ledger = View<"challengeResult">["ledger"];
type LedgerRow = NonNullable<Ledger["bestMove"]>;

// One ledger row's context line: how the player arrived or left, with the
// contract for signings. Engine salary amounts are in thousands.
const ledgerDetail = (row: {
	how?: string;
	pickNum?: number;
	salary?: number;
}): string | undefined => {
	const salary =
		row.salary !== undefined
			? ` · ${helpers.formatCurrency(row.salary / 1000, "M")}`
			: "";
	switch (row.how) {
		case "resigned":
			return `Re-signed${salary}`;
		case "signed":
			return `Signed${salary}`;
		case "traded":
			return "Trade";
		case "drafted":
			return row.pickNum !== undefined ? `Drafted #${row.pickNum}` : "Drafted";
		case "walked":
			return "Let walk";
		default:
			return undefined;
	}
};

// The "HOW YOU SCORED" bars + won-vs-projected strip. mode "pace" is the
// mid-season Progress Report: `won` is the extrapolated full-season pace.
export const ReportBars = ({
	card,
	won,
	projectedWins,
	mode = "final",
}: {
	card: Card;
	won: number;
	projectedWins: number | undefined;
	mode?: "final" | "pace";
}) => {
	const over =
		projectedWins !== undefined
			? Math.round((won - projectedWins) * 10) / 10
			: undefined;
	return (
		<>
			<div className="chr-bars">
				{card.categories.map((c) => {
					const frac =
						c.max > 0 ? Math.min(Math.max(c.points, 0) / c.max, 1) : 0;
					return (
						<div key={c.key}>
							<div className="chr-bar-head">
								<div className="chr-bar-label">{c.label}</div>
								<div className="chr-bar-points">
									{c.points > 0 ? "+" : ""}
									{c.points}
								</div>
							</div>
							<div className="chr-bar-track">
								<div
									className={`chr-bar-fill ${c.points < 0 ? "neg" : ""}`}
									style={{ width: `${Math.max(frac * 100, 1)}%` }}
								/>
							</div>
						</div>
					);
				})}
			</div>
			<div className="chr-over">
				<span>
					{mode === "pace" ? `On pace for ${won}` : `Won ${won}`} · projected{" "}
					{projectedWins ?? "—"} wins
				</span>
				{over !== undefined ? (
					<b>
						{over > 0 ? "+" : ""}
						{over} {over >= 0 ? "over" : "under"} the line
					</b>
				) : null}
			</div>
		</>
	);
};

const LEDGER_ROWS: {
	key: keyof Ledger;
	tag: string;
	icon: string;
	tint: string;
	positive: boolean;
}[] = [
	{
		key: "bestMove",
		tag: "BEST MOVE",
		icon: "📈",
		tint: "#20301f",
		positive: true,
	},
	{
		key: "gotAway",
		tag: "THE ONE THAT GOT AWAY",
		icon: "🚪",
		tint: "#33231e",
		positive: false,
	},
	{
		key: "carried",
		tag: "CARRIED THE TEAM",
		icon: "🏆",
		tint: "#1e2a33",
		positive: true,
	},
	{
		key: "anchor",
		tag: "THE ANCHOR",
		icon: "⚓",
		tint: "#2a2340",
		positive: false,
	},
];

const ledgerValue = (key: keyof Ledger, row: LedgerRow): string =>
	key === "anchor" ? `${row.pm} +/-` : `+${row.ewa} wins`;

const ledgerContext = (
	key: keyof Ledger,
	row: LedgerRow,
): string | undefined => {
	if (key === "anchor") {
		return "Worst on-court +/-";
	}
	if (key === "gotAway") {
		return `${ledgerDetail(row) ?? "Left"} · produced elsewhere`;
	}
	return ledgerDetail(row);
};

// The FRONT OFFICE LEDGER card. Renders nothing when the season has no story.
export const FrontOfficeLedgerCard = ({ ledger }: { ledger: Ledger }) => {
	const rows = LEDGER_ROWS.filter((r) => ledger[r.key]);
	if (rows.length === 0) {
		return null;
	}
	return (
		<div className="chr-card">
			<div className="chr-card-label">FRONT OFFICE LEDGER</div>
			<div className="chr-ledger">
				{rows.map((r) => {
					const row = ledger[r.key]!;
					const context = ledgerContext(r.key, row);
					return (
						<div key={r.key} className="chr-ledger-row">
							<div className="chr-ledger-icon" style={{ background: r.tint }}>
								{r.icon}
							</div>
							<div className="chr-ledger-main">
								<div className="chr-ledger-tag">{r.tag}</div>
								<div className="chr-ledger-name">{row.name}</div>
								{context ? (
									<div className="chr-ledger-detail">{context}</div>
								) : null}
							</div>
							<div className={`chr-ledger-value ${r.positive ? "pos" : "neg"}`}>
								{ledgerValue(r.key, row)}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

export type ReportPlayer = {
	pid: number;
	name: string;
	ovr: number;
	pot: number;
	gp: number;
	pts: number;
	trb: number;
	ast: number;
};

const fmt1 = (x: number) => x.toFixed(1);

// The rotation driving the season: fuzzed OVR/POT + per-game production.
export const TopPlayersCard = ({ players }: { players: ReportPlayer[] }) => {
	if (players.length === 0) {
		return null;
	}
	return (
		<div className="chr-card">
			<div className="chr-card-label">THE ROTATION</div>
			<div className="chr-pl-cols">
				<div>PLAYER</div>
				<div className="c">OVR</div>
				<div className="c">POT</div>
				<div className="c">PPG</div>
				<div className="c">RPG</div>
				<div className="c">APG</div>
			</div>
			<div className="chr-pl-rows">
				{players.map((p) => (
					<div key={p.pid} className="chr-pl-row">
						<div className="chr-pl-name">{p.name}</div>
						<div className="chr-pl-ovr">{p.ovr}</div>
						<div className="chr-pl-pot">{p.pot}</div>
						<div className="chr-pl-stat">{fmt1(p.pts)}</div>
						<div className="chr-pl-stat">{fmt1(p.trb)}</div>
						<div className="chr-pl-stat">{fmt1(p.ast)}</div>
					</div>
				))}
			</div>
		</div>
	);
};

export const ReportBitsStyles = () => <style>{styles}</style>;

const styles = `
.chr-card { margin-top:14px; background:#181818; border:1px solid #232323; border-radius:14px;
  padding:15px 16px; }
.chr-card-label { font-size:10px; font-weight:800; letter-spacing:.8px; color:#9fb0bc; }
.chr-bars { margin-top:12px; display:flex; flex-direction:column; gap:13px; }
.chr-bar-head { display:flex; justify-content:space-between; align-items:baseline; }
.chr-bar-label { font-size:12.5px; font-weight:700; color:#dfe6ea; }
.chr-bar-points { font-size:12px; font-weight:900; color:#fff; }
.chr-bar-track { margin-top:6px; height:7px; border-radius:4px; background:rgba(255,255,255,.08);
  overflow:hidden; }
.chr-bar-fill { height:100%; border-radius:4px; background:#18E0A8; }
.chr-bar-fill.neg { background:#E2504E; }
.chr-over { margin-top:14px; padding-top:12px; border-top:1px solid #232323; display:flex;
  align-items:center; justify-content:space-between; }
.chr-over span { font-size:11.5px; color:#9fb0bc; font-weight:600; }
.chr-over b { font-size:12px; font-weight:900; color:#18E0A8; }
.chr-ledger { margin-top:12px; display:flex; flex-direction:column; gap:11px; }
.chr-ledger-row { display:flex; align-items:center; gap:12px; }
.chr-ledger-row + .chr-ledger-row { border-top:1px solid #232323; padding-top:11px; }
.chr-ledger-icon { width:34px; height:34px; border-radius:9px; display:flex; align-items:center;
  justify-content:center; font-size:16px; flex:none; }
.chr-ledger-main { flex:1; min-width:0; }
.chr-ledger-tag { font-size:9px; font-weight:800; letter-spacing:.7px; color:#7f8b93; }
.chr-ledger-name { font-size:13.5px; font-weight:800; color:#fff; margin-top:2px;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.chr-ledger-detail { font-size:11px; color:#9a9a9a; font-weight:600; margin-top:1px; }
.chr-ledger-value { font-size:12.5px; font-weight:900; flex:none; }
.chr-ledger-value.pos { color:#18E0A8; }
.chr-ledger-value.neg { color:#E2504E; }
.chr-pl-cols { display:grid; grid-template-columns:1fr 36px 36px 44px 44px 44px; gap:4px;
  padding:12px 2px 7px; font-size:9px; font-weight:800; letter-spacing:.5px; color:#7f8b93; }
.chr-pl-cols .c { text-align:center; }
.chr-pl-rows { display:flex; flex-direction:column; }
.chr-pl-row { display:grid; grid-template-columns:1fr 36px 36px 44px 44px 44px; gap:4px;
  align-items:center; padding:8px 2px; border-top:1px solid #232323; }
.chr-pl-name { font-size:12.5px; font-weight:800; color:#e6ebee; white-space:nowrap;
  overflow:hidden; text-overflow:ellipsis; }
.chr-pl-ovr { font-size:12.5px; font-weight:900; color:#fff; text-align:center; }
.chr-pl-pot { font-size:12px; font-weight:700; color:#8a97a0; text-align:center; }
.chr-pl-stat { font-size:12px; font-weight:700; color:#b0b8bd; text-align:center;
  font-variant-numeric:tabular-nums; }
`;
